import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { AuthProvider } from "./context/AuthContext";
import { PlayerProvider } from "./context/PlayerContext";
import { SettingsProvider } from "./context/SettingsContext";
import { captureUtm } from "./lib/utm";
import PersistentPlayer from "./components/PersistentPlayer";
import MobileStickyFollow from "./components/MobileStickyFollow";
import Trackers from "./components/Trackers";
import ConsentBanner from "./components/ConsentBanner";
import Home from "./pages/Home";
import ReleaseDetail from "./pages/ReleaseDetail";
import CampaignPage from "./pages/CampaignPage";
import Thanks from "./pages/Thanks";
import NotFound from "./pages/NotFound";
import About from "./pages/About";
import Press from "./pages/Press";
import { Videos, VideoDetail } from "./pages/Videos";
import { News, NewsDetail } from "./pages/News";
import Nav from "./components/Nav";
import AuthCallback from "./pages/AuthCallback";
import AdminApp from "./pages/admin/AdminApp";
import { Privacy, Terms } from "./pages/StaticPages";

function PublicPlayer() {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  return (
    <>
      <PersistentPlayer />
      <MobileStickyFollow />
      <ConsentBanner />
    </>
  );
}

// Scroll to #anchors after route changes (e.g. /#join from other pages).
function ScrollToHash() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 350);
    return () => clearTimeout(t);
  }, [hash, pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Process OAuth callback FIRST (session_id in URL fragment)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Trackers />
      <ScrollToHash />
      <Nav />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/music/:slug" element={<ReleaseDetail />} />
        <Route path="/videos" element={<Videos />} />
        <Route path="/videos/:slug" element={<VideoDetail />} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:slug" element={<NewsDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/press" element={<Press />} />
        <Route path="/epk" element={<Press />} />
        <Route path="/thanks" element={<Thanks />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/admin" element={<AdminApp />} />
        <Route path="/:slug" element={<CampaignPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <PublicPlayer />
    </>
  );
}

function App() {
  useEffect(() => {
    captureUtm();
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    let raf;
    function loop(time) {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <SettingsProvider>
          <AuthProvider>
            <PlayerProvider>
              <AppRouter />
            </PlayerProvider>
          </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
